import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { type PlaybookType, type QuickGuiddeType } from 'app/types'

import { isQG, request } from 'modules'
import { useShouldMatchBrowserLanguage } from './use-should-match-browser-language'

type PlaylistItemType = PlaybookType | QuickGuiddeType

const createCachedTranslations = (playbookList: Array<PlaylistItemType>) => {
    return playbookList.reduce<Record<number, Record<string, PlaylistItemType> | null>>((acc, playbook, index) => {
        if (!isQG(playbook)) {
            return { ...acc, [index]: null }
        }

        const langCode = playbook.language?.langCode || ''

        return {
            ...acc,
            [index]: {
                [langCode]: playbook,
            },
        }
    }, {})
}

const loadPlaybookData = async (playbookId: string) => {
    const url = `/c/v1/quickguidde?id=${playbookId}&forEmbed=true`
    const playbook = await request(url, 'GET')

    if (!playbook) {
        return null
    }

    return playbook?.playbook as PlaylistItemType
}

const getBrowserLanguages = () => {
    if (!navigator) return []

    return navigator.languages || (navigator.language ? [navigator.language] : [])
}

const findBestMatchingLanguageCode = (availableTranslations: string[]) => {
    const browserLanguages = getBrowserLanguages()

    if (!availableTranslations?.length || !browserLanguages?.length) {
        return null
    }

    const exactMatch = availableTranslations.find(lang => lang === browserLanguages[0])

    if (exactMatch) {
        return exactMatch
    }

    const baseMatch = availableTranslations.find(lang => {
        return browserLanguages.find(browserLang => {
            const baseLang = browserLang.split('-')[0].toLowerCase()

            return lang.toLowerCase().includes(baseLang)
        })
    })

    return baseMatch || null
}

export const useTranslations = (playbookList: Array<PlaylistItemType>, playlistLangCode = '') => {
    const shouldMatchBrowserLanguage = useShouldMatchBrowserLanguage()

    const [currentVideoIndex, setCurrentVideoIndex] = useState<number>(0)

    const [cachedTranslations, setCachedTranslations] = useState(createCachedTranslations(playbookList))
    const [activePlaylistLangCode, setActivePlaylistLangCode] = useState('')
    const [playbook, setPlaybook] = useState(playbookList[currentVideoIndex])

    const initialized = useRef(false)

    const getVideoToPlay = useCallback(
        async (videoIndex: number, langCode: string) => {
            const nextVideo = playbookList[videoIndex]

            if (!isQG(nextVideo)) {
                return nextVideo
            }

            const nextVideoTranslations = nextVideo.primaryPlaybook?.translations || nextVideo.translations || []

            const nextVideoByPlaylistLangCode = nextVideoTranslations.find(
                ({ language }) => language.langCode === langCode,
            )

            const cachedVideo = cachedTranslations[videoIndex]?.[langCode]

            if (cachedVideo) {
                return cachedVideo
            }

            const playbookIdToLoad = nextVideoByPlaylistLangCode?.id

            if (!playbookIdToLoad) {
                return nextVideo
            }

            const playbook = await loadPlaybookData(playbookIdToLoad)

            if (!playbook) {
                return nextVideo
            }

            return playbook
        },
        [cachedTranslations, playbookList],
    )

    const addNewVideoToCache = useCallback((videoIndex: number, video: PlaylistItemType, langCode: string) => {
        setCachedTranslations(prev => ({
            ...prev,
            [videoIndex]: {
                ...prev[videoIndex],
                [langCode]: video,
            },
        }))
    }, [])

    const onChangeActiveVideo = useCallback(
        async (videoIndex: number, newLangCode?: string) => {
            const langCode = newLangCode ?? activePlaylistLangCode
            const video = await getVideoToPlay(videoIndex, langCode)

            if (isQG(video)) {
                addNewVideoToCache(videoIndex, video, langCode)
            }

            setCurrentVideoIndex(videoIndex)
            setPlaybook(video)
        },
        [getVideoToPlay, addNewVideoToCache, activePlaylistLangCode, setCurrentVideoIndex],
    )

    const onLanguageChange = useCallback(
        (langCode: string) => {
            onChangeActiveVideo(currentVideoIndex, langCode)
            setActivePlaylistLangCode(langCode)
        },
        [onChangeActiveVideo, currentVideoIndex],
    )

    useEffect(() => {
        if (activePlaylistLangCode || initialized.current || !playbook) return

        const activePlaybook = playbookList[currentVideoIndex]

        const translations = isQG(activePlaybook)
            ? activePlaybook.primaryPlaybook?.translations || activePlaybook.translations || []
            : [
                  {
                      language: {
                          langCode: playbook.language?.langCode || '',
                      },
                  },
              ]

        const availableLangCodes = translations.map(translation => translation.language?.langCode).filter(Boolean)

        const bestMatchingLangCode = shouldMatchBrowserLanguage
            ? findBestMatchingLanguageCode(availableLangCodes)
            : null

        const newLangCode = bestMatchingLangCode || activePlaybook.language?.langCode || playlistLangCode || 'en-US'

        onLanguageChange(newLangCode)

        initialized.current = true
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        playbookList,
        playlistLangCode,
        playbook,
        activePlaylistLangCode,
        currentVideoIndex,
        shouldMatchBrowserLanguage,
    ])

    return useMemo(
        () => ({
            onLanguageChange,
            playbook,

            currentVideoIndex,
            setCurrentVideoIndex,

            onChangeActiveVideo,
        }),
        [playbook, currentVideoIndex, setCurrentVideoIndex, onChangeActiveVideo, onLanguageChange],
    )
}
